import type { PracticeType } from '@packages/types';
import { administeredKindTitle } from '../../lib/practice-settings';

export const ACCOUNT_TABS = [
  // The `id` is a URL parameter and a deep-link target, so it stays `practice`
  // even when the visible label is a kind title or the firm organisation
  // label; see `labelSettingsTabs`.
  { id: 'practice', label: 'Practice' },
  { id: 'billing', label: 'Billing' },
  { id: 'team', label: 'Team' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'profile', label: 'Profile' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'accounting', label: 'Accounting' },
  { id: 'xero', label: 'Xero' },
  { id: 'privacy', label: 'Data & Privacy' },
] as const;

export type AccountTabId = (typeof ACCOUNT_TABS)[number]['id'];

const VALID_TAB_IDS = new Set<string>(ACCOUNT_TABS.map((t) => t.id));

export function isValidTabId(value: string | null): value is AccountTabId {
  return value !== null && VALID_TAB_IDS.has(value);
}

type Access = 'loading' | 'granted' | 'denied';

/** The feature-flag gate that decides whether a settings tab exists at all. */
export type SettingsTabAccess = {
  accountingAccess: Access;
};

/**
 * Whether the account's feature flags let the user reach a tab. The Accounting
 * tab follows its access hook; every other tab is always reachable. `AccountPage` uses this both to hide the tab and to refuse
 * a `?tab=` deep link or tab click to one that is gated off.
 */
export function isSettingsTabAccessible(id: AccountTabId, access: SettingsTabAccess): boolean {
  if (id === 'accounting') return access.accountingAccess === 'granted';
  return true;
}

/**
 * The settings tabs a user sees. Inside an administered entity's books there
 * is no separate team to manage (membership is derived from the administering
 * firm, and the backend refuses member writes on an entity account), no
 * subscription of its own (the firm's plan governs it), and no Xero connection
 * of its own (Xero is one connection per account, and an entity's own
 * connection is not designed yet - connect from the firm's settings instead).
 */
export function visibleSettingsTabs({
  administered,
  ...access
}: SettingsTabAccess & { administered: boolean }): (typeof ACCOUNT_TABS)[number][] {
  const entityFilteredTabs = administered
    ? ACCOUNT_TABS.filter((t) => t.id !== 'team' && t.id !== 'subscription' && t.id !== 'xero')
    : ACCOUNT_TABS;
  return entityFilteredTabs.filter((t) => isSettingsTabAccessible(t.id, access));
}

/**
 * Applies the visible label of the `practice` tab. Firm accounts keep the
 * dialect organisation label ("Practice" / "Business"). An administered entity
 * uses the kind title instead, so a real BUSINESS firm still says Business.
 * Unknown kind: "Not set".
 */
export function labelSettingsTabs<T extends { id: string; label: string }>(
  tabs: readonly T[],
  {
    administered,
    practiceType,
    organisationLabel,
  }: { administered: boolean; practiceType: PracticeType | null; organisationLabel: string },
): T[] {
  return tabs.map((tab) => {
    if (tab.id !== 'practice') return tab;
    if (administered) return { ...tab, label: administeredKindTitle(practiceType) ?? 'Not set' };
    return { ...tab, label: organisationLabel };
  });
}
